<template>
  <div :dir="isRtl !== false ? 'rtl' : 'ltr'" class="relative agent-personality-selector-container" :style="containerStyle">
    <button
      class="btn relative w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0 agent-personality-selector-button"
      type="button"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedPersonalityObject !== null" class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0" style="margin-inline-end: 0.5rem;">
          <Icon :icon="selectedPersonalityObject.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-1 min-w-0 overflow-hidden relative" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full">{{ selectedPersonalityObject.displayName }}</span>
          <span class="block text-xs text-base-content opacity-70 truncate">{{ selectedPersonalityObject.description }}</span>
        </div>
      </div>
      <div v-else class="flex items-center w-full min-w-0" style="padding-inline-end: 1.75rem;">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0" style="margin-inline-end: 0.5rem;">
          <Icon icon="heroicons:user-circle-20-solid" class="w-4 h-4 text-base-content" />
        </span>
        <span class="text-sm text-base-content opacity-70 whitespace-nowrap">{{ placeholderLabel }}</span>
      </div>
      <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 absolute top-1/2 -translate-y-1/2" :class="isRtl === true ? 'left-3' : 'right-3'" />
    </button>
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen === true"
        class="absolute z-20 mt-1 bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 agent-personality-selector-dropdown"
        :style="dropdownStyle"
        ref="dropdownListRef"
      >
        <div class="py-1 space-y-1">
          <div
            v-for="personality in personalityOptions"
            :key="personality.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{ 
              'bg-primary text-primary-content': personality.id === selectedPersonality,
              'hover:bg-base-200 dark:hover:bg-gray-700': personality.id !== selectedPersonality
            }"
            @click="selectPersonalityAndClose(personality)"
          >
            <div class="p-3">
              <div class="flex items-start space-x-3">
                <div class="flex-shrink-0">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600">
                    <Icon :icon="personality.icon" class="w-5 h-5 text-base-content" :class="{'text-primary-content': personality.id === selectedPersonality}" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="[{ 'text-primary-content': personality.id === selectedPersonality }, isRtl === true ? 'text-right' : 'text-left']">{{ personality.displayName }}</h3>
                  <p class="text-xs mt-0.5" :class="[personality.id === selectedPersonality ? 'text-primary-content opacity-80' : 'text-base-content opacity-70', isRtl === true ? 'text-right' : 'text-left']">
                    {{ personality.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';

const props = defineProps({
  selectedPersonality: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['personality-change-requested']);

const { t, currentLanguage } = useI18n();
const isOpen = ref(false);
const dropdownListRef = ref(null);
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

const personalityDefinitions = [
  {
    id: 'CustomerServiceAgent',
    icon: 'heroicons:user-circle-20-solid',
    defaultDisplayName: 'Customer Service Agent',
    defaultDescription: 'Acts as a contact center agent, resolving support queries efficiently to improve customer satisfaction, retention, and enterprise ROI.',
    template: 'You act as a contact center agent, resolving support queries efficiently to improve customer satisfaction, retention, and enterprise ROI.'
  },
  {
    id: 'KnowledgeBaseQA-Agent',
    icon: 'heroicons:book-open-20-solid',
    defaultDisplayName: 'Knowledge Base Q&A Agent',
    defaultDescription: 'Delivers accurate answers based on your documents, ensuring consistency, compliance, and operational efficiency for enterprise knowledge management.',
    template: 'You deliver accurate answers based on your documents, ensuring consistency, compliance, and operational efficiency for enterprise knowledge management.'
  },
  {
    id: 'SalesAgent',
    icon: 'heroicons:currency-dollar-20-solid',
    defaultDisplayName: 'Sales Agent',
    defaultDescription: 'Guides enterprise customers through product solutions, highlighting ROI and business impact to drive conversions and value.',
    template: 'You guide enterprise customers through product solutions, highlighting ROI and business impact to drive conversions and value.'
  },
  {
    id: 'SupportAgent',
    icon: 'heroicons:chat-bubble-left-right',
    defaultDisplayName: 'Support Agent',
    defaultDescription: 'Handles support questions with a calm, practical approach, resolves issues clearly, and explains the next best action when help is needed.',
    template: 'You handle support questions with a calm, practical approach, resolve issues clearly, and explain the next best action when help is needed.'
  }
];

const personalityOptions = computed(() => personalityDefinitions.map((definition) => {
  const displayName = t(`agentPersonalitySelector.personalities.${definition.id}.displayName`, definition.defaultDisplayName);
  const description = t(`agentPersonalitySelector.personalities.${definition.id}.description`, definition.defaultDescription);
  return {
    ...definition,
    displayName,
    description
  };
}));

const selectedPersonalityObject = computed(() => personalityOptions.value.find(option => option.id === props.selectedPersonality) ?? null);
const placeholderLabel = computed(() => t('agentPersonalitySelector.selectPersonality', 'Select personality'));

const containerStyle = computed(() => ({
  width: isOpen.value === true ? '16rem' : (currentLanguage.value === 'en' ? '13.5rem' : '11.5rem'),
  transition: 'width 0.2s ease'
}));
const dropdownStyle = computed(() => ({
  width: isOpen.value === true ? '16rem' : '12rem',
  transition: 'width 0.2s ease'
}));
const arrowSpacingClass = computed(() => (isRtl.value === true ? 'mr-auto' : 'ml-auto'));
const textAlignmentClass = computed(() => (isRtl.value === true ? 'text-right' : 'text-left'));

const toggleDropdown = () => {
  isOpen.value = isOpen.value === false;
};

const selectPersonalityAndClose = (personality) => {
  emit('personality-change-requested', personality);
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  const target = event.target;
  if (dropdownListRef.value !== null && isOpen.value === true) {
    if (dropdownListRef.value.contains(target) === false) {
      const button = target.closest('.agent-personality-selector-button');
      if (button === null) {
        isOpen.value = false;
      }
    }
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});
</script>

<style scoped>
.agent-personality-selector-container {
  transition: width 0.2s ease;
}
.agent-personality-selector-dropdown {
  transition: width 0.2s ease;
}
.icon-spacing {
  margin-inline-end: 12px;
}
</style>

